import {Button, Card, Choice, showNotification} from 'platform/components';
import {Grid, GridItem, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {
  useDeleteVirtualPresetMutation,
  useGetDataGridListQuery,
  useSetActivePresetMutation,
} from '@dms/api/core';
import {handleApiError} from '@dms/shared';

import {getSearchParamsByGridCode, removeSearchParamsByGridCode} from 'features/datagrid';

export function GridPresetReset() {
  const [selectedGridCode, setSelectedGridCode] = useState<string | null>(null);

  const {data: gridList, isLoading: isGridListLoading} = useGetDataGridListQuery({});
  const [setActivePreset, {isLoading: isDeactivating}] = useSetActivePresetMutation();
  const [deleteVirtualPreset, {isLoading: isDeleting}] = useDeleteVirtualPresetMutation();

  const gridOptions =
    gridList?.map((grid) => ({
      label: grid.code,
      value: grid.code,
    })) ?? [];

  const handleReset = () => {
    if (isNil(selectedGridCode)) {
      return;
    }

    const code = selectedGridCode;

    setActivePreset({code, setActivePresetRequestBody: {activePreset: null}})
      .unwrap()
      .then(() => deleteVirtualPreset({code}).unwrap())
      .then(() => {
        // eslint-disable-next-line no-restricted-syntax
        if (getSearchParamsByGridCode(code)?.queryId) {
          removeSearchParamsByGridCode(code);
        }
        setSelectedGridCode(null);
        showNotification.success(`Preset reset for grid "${code}"`);
      })
      .catch(handleApiError);
  };

  const isResetDisabled = isNil(selectedGridCode) || isDeactivating || isDeleting;

  return (
    <Card title="Reset grid preset" variant="inlineGrey">
      <VStack spacing={2}>
        <Grid columns={4} align="flex-end">
          <GridItem span={3}>
            <Choice<string>
              label="Grid code"
              value={selectedGridCode}
              onChange={setSelectedGridCode}
              options={gridOptions}
              isLoading={isGridListLoading}
              data-testid="devSettings-gridPresetReset-gridCode"
            />
          </GridItem>

          <Button
            title="Reset"
            onClick={handleReset}
            isDisabled={isResetDisabled}
            isLoading={isDeactivating || isDeleting}
            leftIcon="action/restore"
            variant="secondary"
            data-testid="devSettings-gridPresetReset-resetButton"
          />
        </Grid>
        <Text size="xSmall" color="tertiary">
          Clears the active custom preset (switching the grid back to the virtual preset) and then
          deletes the virtual preset so the backend regenerates a fresh one. Reload the page after
          running this. Use when a grid is stuck with broken columns, filters or sort.
        </Text>
      </VStack>
    </Card>
  );
}
